import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, CalendarCheck, FileText, Repeat, Trash, CircleNotch, UsersThree, CaretRight } from "@phosphor-icons/react";
import { CustomerProfile } from "@/components/CustomerProfile";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const EMPTY = { name: "", email: "", phone: "", notes: "" };

function CustomerCard({ c, idx, onDelete, onOpen }) {
  return (
    <div className="bk-card p-4 flex flex-wrap items-start justify-between gap-3 cursor-pointer bk-lift" data-testid={`customer-row-${idx}`} onClick={() => onOpen(c)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(c)}>
      <div className="min-w-0">
        <p className="font-semibold text-sm" style={{ color: "var(--bk-text)" }} data-testid={`customer-name-${idx}`}>{c.name || "Unnamed customer"}</p>
        <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>{[c.email, c.phone].filter(Boolean).join(" · ") || "No contact details yet"}</p>
        {c.notes && <p className="text-xs mt-1 italic" style={{ color: "var(--bk-text-2)" }}>{c.notes}</p>}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {c.sources.map((s) => (
            <span key={s} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s === "saved" ? "" : "capitalize"}`} style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>{s === "saved" ? "In your list" : s}</span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs" style={{ color: "var(--bk-text-2)" }}>
        <span className="inline-flex items-center gap-1" title="Invoices"><FileText size={14} weight="duotone" /> {c.invoice_count}</span>
        <span className="inline-flex items-center gap-1" title="Bookings"><CalendarCheck size={14} weight="duotone" /> {c.booking_count}</span>
        <span className="inline-flex items-center gap-1" title="Active packages & memberships"><Repeat size={14} weight="duotone" /> {c.active_credits}</span>
        <span className="font-semibold" style={{ color: c.open_balance > 0 ? "#B23B1F" : "var(--bk-text)" }} data-testid={`customer-balance-${idx}`}>{c.open_balance > 0 ? `${money(c.open_balance)} owed` : "Paid up"}</span>
        {c.saved && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(c); }} aria-label="Remove customer" data-testid={`customer-delete-${idx}`} className="p-1 rounded hover:bg-[#FCEEE8]" style={{ color: "var(--bk-energy)" }}><Trash size={15} /></button>
        )}
        <CaretRight size={14} weight="bold" color="var(--bk-text-2)" />
      </div>
    </div>
  );
}

export function CustomersDirectory({ businessId, openAddNonce = 0, onGoToView }) {
  const [profile, setProfile] = useState(null);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const handledNonce = useRef(openAddNonce);

  const load = () => api.get(`/businesses/${businessId}/customers`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (openAddNonce && openAddNonce !== handledNonce.current) { handledNonce.current = openAddNonce; setOpen(true); } }, [openAddNonce]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((c) => !s || `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(s));
  }, [rows, q]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Add the customer's name.");
    setSaving(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/customers`, form);
      toast.success(data.rowan_message || "Customer added");
      setForm(EMPTY); setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add customer");
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/businesses/${businessId}/customers/${c.id}`);
      load();
      toast.success(`Removed ${c.name}`, {
        duration: 8000,
        action: { label: "Undo", onClick: async () => {
          try { await api.post(`/businesses/${businessId}/customers`, { name: c.name, email: c.email, phone: c.phone, notes: c.notes }); toast.success(`${c.name} is back`); load(); }
          catch { toast.error("Couldn't restore — add them again from Add customer."); }
        } },
      });
    } catch { toast.error("Couldn't remove customer"); }
  };

  return (
    <div className="space-y-4" data-testid="customers-directory">
      <div className="bk-card p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Your customers</h3>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Everyone you've invoiced, booked or sold a package to — plus anyone you add by hand. Click a name to see their full history.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" color="var(--bk-text-2)" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers" data-testid="customer-search-input" className="rounded-full border pl-9 pr-4 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          </label>
        </div>
      </div>

      {rows === null ? (
        <p className="text-sm inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}><CircleNotch size={16} className="animate-spin" /> Loading customers…</p>
      ) : filtered.length === 0 ? (
        <div className="bk-card p-10 text-center" data-testid="customers-empty">
          <UsersThree size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>{q ? "No one matches that search." : "No customers yet."}</p>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{q ? "Try a different name or email." : "Add your first customer, or send an invoice and they'll appear here automatically."}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="customer-list">
          {filtered.map((c, i) => <CustomerCard key={c.id || `${c.name}-${c.email}-${i}`} c={c} idx={i} onDelete={remove} onOpen={setProfile} />)}
        </div>
      )}

      <CustomerProfile businessId={businessId} customer={profile} onClose={() => setProfile(null)} onGoToView={onGoToView} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-customer-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--bk-text)" }}>Add a customer</DialogTitle>
            <DialogDescription>Just a name is enough. Email lets Rowan link their invoices automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[["name", "Name", "text"], ["email", "Email", "email"], ["phone", "Phone", "tel"]].map(([k, label, type]) => (
              <label key={k} className="block text-xs" style={{ color: "var(--bk-text-2)" }}>{label}
                <input type={type} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`customer-${k}-input`} className="w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
              </label>
            ))}
            <label className="block text-xs" style={{ color: "var(--bk-text-2)" }}>Notes
              <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} data-testid="customer-notes-input" className="w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
            </label>
            <button onClick={save} disabled={saving} data-testid="save-customer-btn" className="bk-btn-primary w-full py-2.5 text-sm font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save customer"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
